// Companion/blocks/StudySetFlashcardDeck.tsx
//
// The flashcard half of a study set: a compact deck the student can actually
// rate from, rather than the flip-and-next preview a served `flashcards` block
// draws. The rating is real — same FSRS step, same `respond` write the moment
// the student gives it, the day's flashcard count goes up — so cards cleared
// here are cleared for good, which is the whole point of putting a set in the
// thread.
//
// It is a real *session*, too (ORA-687): the deck drives the full flashcard
// screen's own reducer, so Again brings the card back, a card still due today
// comes back, and the deck is finished only when the queues are empty. The
// reducer runs locally through `useReducer` rather than on the store, because
// the drawer opens over the study routes and a full session may be holding
// `state.flashcardRun` on the page behind the deck.

import {
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useSelector } from 'react-redux';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { parseHtmlWithMathLatex } from '@/utils/htmlParser';
import FlashcardFace from '@/components/ora/Study/Flashcards/FlashcardFace';
import AnimatedCheckmark from '@/components/ora/Global/AnimatedCheckmark';
import RatingFeedbackBubble from '@/components/ora/Study/Flashcards/RatingFeedbackBubble';
import {
  formatFlashcardInterval,
  ratingButtonsFor,
} from '@/components/ora/Study/Flashcards/flashcardRatings';
import flashcardRunReducer, {
  flashcardRunBootstrapped,
  flashcardRunResponseApplied,
  initialState as flashcardRunInitialState,
  queueTargetForScheduling,
} from '@/slices/flashcardRun/flashcardRunSlice';
import type { RatingFeedback } from '@/hooks/useFlashcardResponse';
import type { RootState } from '@/store/store';
import type { FlashcardScheduling } from '@/types/Flashcard';
import type { ChatStudySetFlashcard } from '@/types/chatBlocks';
import {
  bootstrapFlashcardRunFromSetCards,
  nextSetCardScheduling,
  sourceQueueFor,
} from './studySetFlashcardRun';
import { useStudySetFlashcardRating } from './useStudySetFlashcardRating';
import { resolveDesiredRetention } from '@/services/scheduler';

/**
 * What the badge over a card says about where it sits in the student's queue.
 * It reads the run's scheduling rather than the served one, so a card that came
 * back after an Again is badged for where it is now.
 */
const cardStatus = (
  scheduling: FlashcardScheduling | null | undefined
): 'New' | 'Due' | 'Scheduled' => {
  if (!scheduling || scheduling.state === 0) return 'New';

  const due = new Date(scheduling.due).getTime();
  if (Number.isNaN(due)) return 'Due';

  return due <= Date.now() ? 'Due' : 'Scheduled';
};

/**
 * The requestId the run engine stamps on a pending response. The deck writes
 * its own ratings rather than draining the run's pending queue, so this only
 * has to be unique inside the local run.
 */
const localRequestId = (cardId: string) =>
  `study-set-${cardId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * How long the interval bubble stays mounted after a rating: the length of its
 * `rating-feedback-pop` animation plus a frame, as on the full flashcard
 * screen, so the next rating mounts a fresh bubble and replays it.
 */
const RATING_FEEDBACK_DURATION_MS = 850;

export interface StudySetFlashcardDeckProps {
  setId: string;
  cards: ChatStudySetFlashcard[];
  /**
   * Cards the server had already marked done. They start outside the queues
   * rather than at the front of them: rating one again would write a second
   * review and count it twice against the set's progress.
   */
  completedCardIds?: string[];
  /**
   * Reports each card that has left the queues, so the set's progress can move.
   * A card rated Again is still in the session and does not report.
   */
  onCardGraduated?: (cardId: string) => void;
  /**
   * What is left in the queues, reported up because the set's header — not the
   * deck — is where the student's place in a set is written (ORA-685).
   */
  onCountsChange?: (counts: { new: number; due: number }) => void;
  /**
   * The card on screen and whether its back is showing, for a host that
   * publishes the companion's anchor itself (a course step, Courses V1). Null
   * once the queues are empty.
   */
  onCurrentCardChange?: (
    card: ChatStudySetFlashcard | null,
    isRevealed: boolean
  ) => void;
  /**
   * Holds the interval bubble up instead of clearing it after its animation.
   * For the Storybook story that exists to show the bubble (ORA-833): 850ms is
   * not long enough for a human to look at it, let alone screenshot it. Nothing
   * in the app passes it.
   */
  holdRatingFeedback?: boolean;
  /**
   * How the deck takes height. `fixed` (the chat block) gives the card a set
   * height so a thread's block stays the same size front and back. `fill` (a
   * course step, Courses V1) lets the deck take the height its host offers:
   * the card grows into it and scrolls inside itself, and the actions sit at
   * the bottom.
   */
  layout?: 'fixed' | 'fill';
}

const StudySetFlashcardDeck = ({
  setId,
  cards,
  completedCardIds,
  onCardGraduated,
  onCountsChange,
  onCurrentCardChange,
  holdRatingFeedback = false,
  layout = 'fixed',
}: StudySetFlashcardDeckProps) => {
  const { rate, responseOptions } = useStudySetFlashcardRating(setId);

  // The same preference the full flashcard screen reads. The server default is
  // on, so an account that has never touched the setting sees the intervals.
  const showIntervals = useSelector(
    (state: RootState) => state.user.profile?.showIntervals ?? true
  );

  const desiredRetentionValue = useSelector(
    (state: RootState) => state.user.profile?.desiredRetention
  );

  const desiredRetention = resolveDesiredRetention(desiredRetentionValue);

  const [run, dispatchRun] = useReducer(
    flashcardRunReducer,
    { setId, cards, completedCardIds },
    (params) =>
      flashcardRunReducer(
        flashcardRunInitialState,
        flashcardRunBootstrapped(bootstrapFlashcardRunFromSetCards(params))
      )
  );

  const [showBack, setShowBack] = useState(false);

  // The full flashcard screen's own bubble state (ORA-833): the deck pops the
  // same component, one size down.
  const [ratingFeedback, setRatingFeedback] = useState<RatingFeedback | null>(
    null
  );

  const ratingFeedbackKeyRef = useRef(0);

  // Cleared once the animation has played, so it unmounts and the next rating
  // mounts a fresh one — the full flashcard screen's own rule.
  useEffect(() => {
    if (!ratingFeedback || holdRatingFeedback) return;

    const timeoutId = window.setTimeout(
      () => setRatingFeedback(null),
      RATING_FEEDBACK_DURATION_MS
    );

    return () => window.clearTimeout(timeoutId);
  }, [holdRatingFeedback, ratingFeedback]);

  const ratings = useMemo(
    () => ratingButtonsFor(responseOptions),
    [responseOptions]
  );

  const cardsById = useMemo(
    () => new Map(cards.map((entry) => [entry.id, entry])),
    [cards]
  );

  // The face comes from the set payload, not from the run: the engine only
  // carries what it needs to schedule a card.
  const card = run.currentCardId ? cardsById.get(run.currentCardId) : undefined;
  const isDone = !card;
  /** The same counter semantics as the full session (`selectFlashcardRunCounts`). */
  const newCount = run.newQueueIds.length;
  const dueCount = run.priorityDueIds.length + run.deferredDueIds.length;

  useEffect(() => {
    onCountsChange?.({ new: newCount, due: dueCount });
  }, [newCount, dueCount, onCountsChange]);

  useEffect(() => {
    onCurrentCardChange?.(card ?? null, showBack);
  }, [card, onCurrentCardChange, showBack]);

  /**
   * What each rating would do to this card, to be printed under the button that
   * does it. The step runs off the scheduling the card was served with, exactly
   * as `handleRate` does, so the label says what the button is about to write.
   */
  const intervalLabels = useMemo(() => {
    if (!showIntervals || !showBack || !card) return null;

    const now = new Date();
    const labels: Record<number, string> = {};

    ratings.forEach((rating) => {
      const scheduling = nextSetCardScheduling(
        card,
        rating.response,
        desiredRetention
      );

      if (!scheduling) return;

      labels[rating.response] = formatFlashcardInterval(scheduling.due, now);
    });

    return labels;
  }, [card, desiredRetention, ratings, showBack, showIntervals]);

  const handleRate = (response: number) => {
    if (!card) return;

    const runCard = run.cardsById[card.id];
    if (!runCard) return;

    // Taken before the step, as `intervalLabels` takes it: the step stamps its
    // due off a later clock, so a clock read after it would floor a whole 32
    // days down to 31.
    const now = new Date();
    const scheduling = nextSetCardScheduling(card, response, desiredRetention);
    if (!scheduling) return;

    // The engine's own rule, asked before the write rather than after it: the
    // server marks the set item done on any respond carrying the set id, so the
    // writer needs to know now whether this rating is the card's last.
    const graduated = queueTargetForScheduling(scheduling) === 'completedIds';

    rate(card, response, { scheduling, graduated });

    dispatchRun(
      flashcardRunResponseApplied({
        requestId: localRequestId(card.id),
        sourceQueue: sourceQueueFor(run, card.id),
        updatedCard: { ...runCard, scheduling },
        responseNum: response,
        duration: null,
        flashcardVariantId: card.variantId || undefined,
        studySetId: setId,
      })
    );

    if (graduated) onCardGraduated?.(card.id);

    // The label the grade was printed with, worded as the full screen's
    // bubble words it (`useFlashcardResponse`). With the intervals turned off
    // there is no printed label, but the full screen pops the bubble either
    // way, so it is read off the step just written.
    const intervalLabel =
      intervalLabels?.[response] ??
      formatFlashcardInterval(scheduling.due, now);

    ratingFeedbackKeyRef.current += 1;

    setRatingFeedback({
      key: ratingFeedbackKeyRef.current,
      isPositive: response === 2 || response === 3,
      label: intervalLabel.replace('m', 'min'),
    });

    setShowBack(false);
  };

  /**
   * Space flips the card, exactly as the served flashcards block does — and
   * only when the deck itself has focus, so a space on a rating button is that
   * button's own activation rather than two things at once.
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== ' ' && event.code !== 'Space') return;
    if (event.target !== event.currentTarget) return;

    event.preventDefault();
    if (isDone || showBack) return;
    setShowBack(true);
  };

  return (
    <div
      aria-label="Flashcards"
      className={cn(
        'flex flex-col gap-3',
        layout === 'fill' && 'min-h-0 flex-1'
      )}
      data-testid="study-set-deck"
      data-layout={layout}
      onKeyDown={handleKeyDown}
      role="group"
      tabIndex={0}
    >
      {isDone ? (
        layout === 'fill' ? (
          // A course step's deck (Courses V1) ends on the shared animated
          // check, centred in the space the card took.
          <div
            className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 text-center"
            data-testid="deck-complete"
          >
            <div className="size-20 text-fg-primary" aria-hidden="true">
              <AnimatedCheckmark />
            </div>
            <p className="!m-0 text-fg-secondary">Flashcard Set Complete</p>
          </div>
        ) : (
          <p className="m-0 text-sm text-fg-secondary">
            {run.completedCount > 0
              ? `You rated ${run.completedCount} ${run.completedCount === 1 ? 'card' : 'cards'}.`
              : 'These cards are already rated.'}
          </p>
        )
      ) : (
        // A fixed card area, as on the full flashcard screen
        // (`.flashcard_main_card_container`): short content no longer shrinks
        // the card to a couple of lines, long content scrolls inside it, and
        // front and back are the same height, so revealing the answer does not
        // move the rating row out from under the student's thumb. The padding
        // stays on the scrolling element so the text clears the border at both
        // ends of the scroll.
        <div
          className={cn(
            'overflow-y-auto rounded-lg border border-border-subtle bg-surface-muted text-fg-primary',
            // A course step has the room: the card reads at study size.
            layout === 'fill'
              ? 'min-h-0 flex-1 p-4 text-lg md:p-6 md:text-xl'
              : 'h-64 p-3 text-sm mobile:h-72'
          )}
          data-testid="deck-card"
        >
          {/* Floated rather than laid over the card: the text wraps around the
              badge instead of running under it, and no right gutter is kept
              empty on the lines below it. */}
          {/* A course step's card (Courses V1) takes the badge in outline:
              the secondary fill is the card's own muted surface, so it read
              as bare text at study size. */}
          <Badge
            className="float-right ml-2"
            variant={layout === 'fill' ? 'outline' : 'secondary'}
            data-testid="deck-card-status"
          >
            {cardStatus(run.cardsById[card.id]?.scheduling ?? card.scheduling)}
          </Badge>
          <div data-testid="deck-card-face">
            {/* Keyed per card and face: SafeHtmlParser keeps its parsed
                output in state, so a reused instance would show the last
                face for a frame under the new wrapper. */}
            <FlashcardFace
              key={`${card.id}:${showBack ? 'back' : 'front'}`}
              face={showBack ? 'back' : 'front'}
              html={(showBack ? card.back : card.front) || ''}
            />
          </div>
          {showBack && card.explanation ? (
            // Ruled off from the answer the way the full screen does it
            // (`&_explanation`), and inside the scroll area, so the divider
            // travels with the content rather than pinning itself to the card.
            // A div, not a p: the explanation is HTML.
            <div
              className={cn(
                'mt-3 border-t border-border-subtle pt-3 text-fg-secondary',
                layout === 'fill' ? 'text-base md:text-lg' : 'text-xs'
              )}
              data-testid="deck-card-explanation"
            >
              {parseHtmlWithMathLatex(card.explanation)}
            </div>
          ) : null}
        </div>
      )}

      {!isDone || ratingFeedback ? (
        // The controls sit in a positioned wrapper that outlives the rating
        // row: rating a card flips it back at once, which unmounts the grades,
        // so the bubble hangs off the wrapper instead and centres over the
        // whole row — the full flashcard screen's own anchoring (ORA-833).
        // Once the last card is rated the wrapper holds only the bubble, so it
        // cancels the deck's gap: an empty flex item would add one, and the
        // deck would shrink by it when the bubble clears.
        <div className={cn('relative', isDone && '-mt-3')}>
          {!isDone && !showBack ? (
            <Button
              className="min-h-11 w-full"
              onClick={() => setShowBack(true)}
              type="button"
            >
              Reveal
            </Button>
          ) : null}
          {!isDone && showBack ? (
            // One row, four equal columns, whatever the width: a zero basis with
            // `min-w-0` lets the buttons share the line evenly instead of the
            // fourth dropping under the other three on a 390px phone (staging
            // smoke, 2026-09-17).
            <div
              className="flex w-full min-w-0 gap-2"
              role="group"
              aria-label="Rate this card"
            >
              {ratings.map((rating) =>
                layout === 'fill' ? (
                  // A course step (Courses V1) takes the study session's
                  // rating row (`.flashcard_actions`): a primary button with
                  // what it would do printed under it, not inside it, so the
                  // control announces itself as the grade alone.
                  <div
                    key={rating.response}
                    className="relative flex min-w-0 basis-0 grow"
                  >
                    <Button
                      className="min-h-11 w-full min-w-0 basis-0 grow"
                      onClick={() => handleRate(rating.response)}
                      type="button"
                    >
                      {rating.label}
                    </Button>
                    {/* Hung under the button rather than stacked beneath it,
                        so the row is exactly the Reveal button's height and
                        the grades line up with it; the interval may dip into
                        the host's padding. */}
                    <span
                      aria-hidden="true"
                      className="absolute left-1/2 top-full mt-1 -translate-x-1/2 whitespace-nowrap font-heading text-xs font-medium leading-4 text-fg-primary"
                      data-testid="deck-rating-interval"
                      data-rating={rating.label}
                    >
                      {intervalLabels?.[rating.response] ?? ''}
                    </span>
                  </div>
                ) : (
                  <Button
                    className="min-h-11 min-w-0 basis-0 grow"
                    key={rating.response}
                    onClick={() => handleRate(rating.response)}
                    type="button"
                    variant="outline"
                  >
                    {/* The grade above, the interval under it — stacked inside one
                      child rather than by turning the button into a column, which
                      is the button's own business (DESIGN.md 11). The second line
                      costs height, not width, so the row keeps its four columns. */}
                    <span className="flex flex-col items-center gap-0.5">
                      {rating.label}
                      {intervalLabels?.[rating.response] ? (
                        // Out of the button's name, as on the full screen, where the
                        // interval is drawn beside the button rather than in it: the
                        // control still announces itself as the grade alone.
                        <span
                          aria-hidden="true"
                          className="text-xxs font-normal leading-none text-fg-secondary"
                        >
                          {intervalLabels[rating.response]}
                        </span>
                      ) : null}
                    </span>
                  </Button>
                )
              )}
            </div>
          ) : null}
          {ratingFeedback ? (
            // Keyed per rating: the pop plays on mount, so a second rating has
            // to replace the bubble rather than update it.
            <RatingFeedbackBubble
              key={ratingFeedback.key}
              feedback={ratingFeedback}
              isDecorative
              size="compact"
              testId="deck-rating-feedback"
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default StudySetFlashcardDeck;
